/**
 * Debug Cache Sync - Dev Mode Only
 * 
 * Syncs React Query cache state to Vite dev server endpoint.
 * Polls for refetch signals from test scripts.
 * 
 * Only active when import.meta.env.DEV === true
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export default function DebugCacheSync() {
  const queryClient = useQueryClient();
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    // Only in dev mode
    if (!import.meta.env.DEV) return;

    const syncCache = async () => {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll();

      const state = {
        queries: queries.map((q) => ({
          queryKey: q.queryKey,
          status: q.state.status,
          dataUpdatedAt: q.state.dataUpdatedAt,
          isFetching: q.state.fetchStatus === 'fetching',
        })),
        staleTimeMs: 60000, // Our configured staleTime
      };

      try {
        await fetch('/__debug/rq', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(state),
        });
      } catch (e) {
        // Ignore errors - dev server might not be ready
      }
    };

    const checkRefetch = async () => {
      try {
        const res = await fetch('/__debug/rq/refetch');
        const data = await res.json();
        
        if (data.signal) {
          if (data.signal.all) {
            // Refetch all queries
            console.info('[DebugCacheSync] Triggering refetch ALL queries');
            await queryClient.invalidateQueries();
          } else if (data.signal.keys?.length > 0) {
            // Refetch specific queries
            for (const key of data.signal.keys) {
              console.info('[DebugCacheSync] Triggering refetch for:', key);
              await queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] });
            }
          }
          // Sync after refetch
          setTimeout(syncCache, 500);
        }
      } catch (e) {
        // Ignore
      }
    };

    // Initial sync
    syncCache();

    // Sync every second
    intervalRef.current = window.setInterval(() => {
      syncCache();
      checkRefetch();
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [queryClient]);

  // No UI - just background sync
  return null;
}
